import type { CollectionName } from '../store/types.ts';

/**
 * Mapping between the local camelCase model and the snake_case Postgres
 * columns, declared once per collection so pull and push cannot drift apart.
 *
 * Only fields listed here cross the wire. `id`, `updated_at` and `deleted_at`
 * are handled by the envelope and never appear in these lists.
 */

export interface TableSpec {
  /** Postgres table name. */
  table: string;
  /** localField -> remoteColumn for everything except the envelope. */
  fields: Readonly<Record<string, string>>;
}

const LESSON_FIELDS = {
  day: 'day',
  slot: 'slot',
  subject: 'subject',
  domain: 'domain',
  color: 'color',
  lessonNumber: 'lesson_number',
  title: 'title',
  detail: 'detail',
  competenceIds: 'competence_ids',
  competence: 'competence',
  material: 'material',
  differentiation: 'differentiation',
} as const;

export const TABLES: Readonly<Record<CollectionName, TableSpec>> = {
  students: {
    table: 'students',
    fields: {
      name: 'name',
      firstName: 'first_name',
      lastName: 'last_name',
      birth: 'birth',
      philo: 'philo',
    },
  },
  tasks: {
    table: 'tasks',
    fields: { text: 'text', done: 'done', due: 'due' },
  },
  postits: {
    table: 'postits',
    fields: { text: 'text', color: 'color' },
  },
  lessons: {
    table: 'lessons',
    fields: { weekStart: 'week_start', ...LESSON_FIELDS },
  },
  weekTemplate: {
    table: 'week_template',
    fields: { ...LESSON_FIELDS },
  },
  observations: {
    table: 'observations',
    fields: {
      studentId: 'student_id',
      date: 'date',
      lessonId: 'lesson_id',
      extraLessonIds: 'extra_lesson_ids',
      subject: 'subject',
      difficulty: 'difficulty',
      note: 'note',
      support: 'support',
      hasHandwriting: 'has_handwriting',
      handwritingPath: 'handwriting_path',
    },
  },
  evaluations: {
    table: 'evaluations',
    fields: {
      studentId: 'student_id',
      subject: 'subject',
      domain: 'domain',
      lessonId: 'lesson_id',
      extraLessonIds: 'extra_lesson_ids',
      title: 'title',
      score: 'score',
      max: 'max',
      period: 'period',
      date: 'date',
    },
  },
  meetings: {
    table: 'meetings',
    fields: { date: 'date', title: 'title', notes: 'notes' },
  },
  skills: {
    table: 'skills',
    fields: {
      subject: 'subject',
      status: 'status',
      title: 'title',
      studentId: 'student_id',
    },
  },
};

/** The singleton settings row uses a fixed id rather than a generated one. */
export const SETTINGS_TABLE = 'settings';
export const SETTINGS_ROW_ID = 'settings';
export const SETTINGS_FIELDS = {
  teacher: 'teacher',
  className: 'class_name',
  schoolYear: 'school_year',
} as const;

type Row = Record<string, unknown>;

/** Local row -> Postgres row. */
export function toRemote(fields: Readonly<Record<string, string>>, local: Row): Row {
  const out: Row = {
    id: local['id'],
    updated_at: local['updatedAt'],
    deleted_at: local['deletedAt'] ?? null,
  };
  for (const [localKey, column] of Object.entries(fields)) {
    out[column] = local[localKey];
  }
  return out;
}

/**
 * Postgres timestamps as the local model spells them.
 *
 * PostgREST renders `timestamptz` as `2026-08-23T10:05:00.123456+00:00`, while
 * everything written locally is `new Date().toISOString()` —
 * `2026-08-23T10:05:00.123Z`. Merging compares those strings, and the two
 * spellings do not sort against each other reliably, so every timestamp
 * arriving from the server is canonicalised on the way in. Truncating the
 * microseconds rounds *down*, which at worst re-pulls a row the next time
 * round; rounding up would skip it for good.
 */
export function toIsoString(value: unknown): string | null {
  if (typeof value !== 'string' || value === '') return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString();
}

/** Postgres row -> local row, with the sync envelope reset to "clean". */
export function toLocal(fields: Readonly<Record<string, string>>, remote: Row): Row {
  const out: Row = {
    id: remote['id'],
    updatedAt: toIsoString(remote['updated_at']) ?? '',
    deletedAt: toIsoString(remote['deleted_at']),
    dirty: false,
  };
  for (const [localKey, column] of Object.entries(fields)) {
    out[localKey] = remote[column];
  }
  return out;
}
